import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../api";
import MobileHeader from "../mobile/MobileHeader";
import Login from "../pages/Login"; 
import { COLOR, SHADOW, RADIUS, ThemeProvider, GlobalMobileStyles } from "../mobile/mobiletheme";

const emptyLocation = { building: "", street: "", landmark: "", city: "", postalCode: "" };

export default function CourierOrder() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user_id = localStorage.getItem("phone") || "guest";
  
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  const isGuest = !isLoggedIn || !user_id || user_id === "guest" || user_id === "null" || user_id === "undefined" || String(user_id).trim() === "";

  const selectedRouteId = 1;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [search, setSearch] = useState("");
  const [headerHeight, setHeaderHeight] = useState(112);

  const [contact, setContact] = useState({ fullName: "", phone: "", altPhone: "" });
  
  const [pickup, setPickup] = useState({ ...emptyLocation });
  const [drop, setDrop] = useState({ ...emptyLocation });
  
  const [neededDate, setNeededDate] = useState("");
  const [neededTime, setNeededTime] = useState("");
  const [neededAmPm, setNeededAmPm] = useState("AM");
  
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [showLogin, setShowLogin] = useState(false);

  const goSearch = () => {
    if (search.trim() !== "") navigate(`/search?q=${encodeURIComponent(search.trim())}`);
  };

  const handleContactChange = (e) => setContact({ ...contact, [e.target.name]: e.target.value });
  const handlePickupChange = (e) => setPickup({ ...pickup, [e.target.name]: e.target.value });
  const handleDropChange = (e) => setDrop({ ...drop, [e.target.name]: e.target.value });

  const buildFullAddress = (loc) =>
    `${loc.building}, ${loc.street}, ${loc.landmark ? loc.landmark + ", " : ""}${loc.city}${loc.postalCode ? " - " + loc.postalCode : ""}`;

  const to24HourTime = (time12, ampm) => {
    const [hoursStr, minutesStr] = time12.split(":");
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10) || 0;
    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const formatTimeInput = (raw) => {
    const digits = raw.replace(/[^0-9]/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };

  const handleSubmit = async () => {
    if (isGuest) {
      setError(t("mobile.pleaseLogin"));
      setShowLogin(true);
      return;
    }
    
    if (!contact.fullName || !contact.phone || !contact.altPhone) {
      setError(t("mobile.pleaseNamePhoneAlt"));
      return;
    }
    if (!pickup.building || !pickup.street || !pickup.city) {
      setError(t("mobile.pickupRequired"));
      return;
    }
    if (!drop.building || !drop.street || !drop.city) {
      setError(t("mobile.dropRequired"));
      return;
    }
    if (!neededDate || !neededTime) {
      setError(t("mobile.neededDateTime"));
      return;
    }
    if (!/^([1-9]|1[0-2]):[0-5][0-9]$/.test(neededTime)) {
      setError(t("mobile.validTime"));
      return;
    }

    setError("");
    setSubmitting(true);
    
    const combinedNeededBy = `${neededDate} ${to24HourTime(neededTime, neededAmPm)}`;

    try {
      const res = await axios.post(`${API_URL}/courier`, {
        user_id,
        route_id: selectedRouteId,
        pickup_address: buildFullAddress(pickup),
        drop_address: buildFullAddress(drop),
        needed_by: combinedNeededBy,
        notes: notes,
        name: contact.fullName,
        phone_number: contact.phone,
        alt_phone_num: contact.altPhone,
        email: user_id, 
        building_name: drop.building,
        street: drop.street,
        landmark: drop.landmark,
        city_or_village: drop.city,
        pin_code: drop.postalCode,
        state: "Telangana" 
      });
      
      if (res.data.success) {
        setSubmitted(true);
      } else {
        setError(res.data.message || t("mobile.somethingWentWrong"));
      }
    } catch (err) {
      setError(err.response?.data?.message || t("mobile.failedSubmitRequest"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider>
      <GlobalMobileStyles />
    <div style={styles.page}>
      {showLogin && (
        <div style={styles.overlay}>
          <div style={styles.loginBox}>
            <button style={styles.closeBtn} onClick={() => setShowLogin(false)}>×</button>
            <Login onLoginSuccess={() => { 
              setShowLogin(false); 
              window.location.reload(); 
            }} />
          </div>
        </div>
      )}

      <MobileHeader
        searchValue={search}
        setSearchValue={setSearch}
        onSearch={goSearch}
        showLogo={false}
        showTitleBar
        showBackButton={true}
        onBack={() => navigate(-1)}
        title={submitted ? t("mobile.courierRequestTitle") : t("mobile.courierOrderTitle")}
        activeTab="home"
        isLoggedIn={!isGuest}
        onHeightChange={setHeaderHeight}
      />

      <div style={{ ...styles.scrollArea, paddingTop: `${headerHeight + 15}px` }}>
        {isGuest ? (
          <div style={styles.confirmCard}>
            <h2 style={styles.heading}> {t("mobile.loginRequired")} </h2>
            <p style={styles.confirmText}>
              {t("mobile.courierLoginRequired")}
            </p>
            <button style={styles.submitBtn} onClick={() => setShowLogin(true)}> {t("mobile.loginContinue")} </button>
          </div>
        ) : submitted ? (
          <div style={styles.confirmCard}>
            <div style={styles.confirmIcon}>✓</div>
            <h2 style={styles.heading}> {t("mobile.requestReceived")} </h2>
            <p style={styles.confirmText}>
              {t("mobile.courierConfirm")}
            </p>
            <button style={styles.submitBtn} onClick={() => navigate("/")}> {t("mobile.backHome")} </button>
          </div>
        ) : (
          <div style={styles.card}>
            <div style={{ textAlign: "center", marginBottom: "25px" }}>
              <p style={styles.subheading}>
                 {t("mobile.courierSubheading")}
              </p>
            </div>

            <div style={styles.noteCard}>
              <h3 style={styles.noteTitle}> {t("mobile.importantOrderInfo")} </h3>
              <p style={styles.noteText}>
                {t("mobile.garlandNote1")}
              </p>
              <p style={styles.noteText}>
                {t("mobile.garlandNote2")}
              </p>
              <div style={styles.noteContactBox}>
                <span style={styles.noteContactLabel}> {t("mobile.supportContact")} </span>
                <span style={styles.noteContactNumber}>📞 6301912803</span>
              </div>
            </div>

            <div style={styles.instructionBanner}>
              <p style={styles.instructionText}>
                👉 <b>{t("mobile.note")}</b> {t("mobile.cartInstruction")}
              </p>
            </div>

            <div style={styles.contactBlock}>
              <h3 style={styles.sectionTitle}> {t("mobile.contactDetails")} </h3>
              
              <label style={styles.label}> {t("mobile.fullName")} </label>
              <input type="text" name="fullName" value={contact.fullName} onChange={handleContactChange} placeholder={t("mobile.enterFullName")} style={styles.input} />

              <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}> {t("mobile.phone")} </label>
                  <input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} placeholder={t("mobile.egPhone")} style={styles.input} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}> {t("mobile.whatsapp")} </label>
                  <input type="tel" name="altPhone" value={contact.altPhone} onChange={handleContactChange} placeholder={t("mobile.egPhone")} style={styles.input} />
                </div>
              </div>
            </div>

            <div style={styles.pickupBlock}>
              <h3 style={styles.sectionTitle}> {t("mobile.pickupAddress")} </h3>
              
              <label style={styles.label}>{t("mobile.buildingFlat")}</label>
              <input type="text" name="building" value={pickup.building} onChange={handlePickupChange} placeholder={t("mobile.egBuilding")} style={styles.input} />

              <label style={{ ...styles.label, marginTop: "12px" }}>{t("mobile.streetColony")}</label>
              <input type="text" name="street" value={pickup.street} onChange={handlePickupChange} placeholder={t("mobile.egStreet")} style={styles.input} />

              <label style={{ ...styles.label, marginTop: "12px" }}>{t("mobile.landmark")}</label>
              <input type="text" name="landmark" value={pickup.landmark} onChange={handlePickupChange} placeholder={t("mobile.egLandmark")} style={styles.input} />

              <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>{t("mobile.city")}</label>
                  <input type="text" name="city" value={pickup.city} onChange={handlePickupChange} placeholder={t("mobile.egCity")} style={styles.input} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>{t("mobile.pincode")}</label>
                  <input type="text" name="postalCode" value={pickup.postalCode} onChange={handlePickupChange} placeholder={t("mobile.egPincode")} style={styles.input} />
                </div>
              </div>
            </div>

            <div style={styles.dropBlock}>
              <h3 style={styles.sectionTitle}> {t("mobile.dropAddress")} </h3>
              
              <label style={styles.label}>{t("mobile.buildingFlat")}</label>
              <input type="text" name="building" value={drop.building} onChange={handleDropChange} placeholder={t("mobile.egBuilding")} style={styles.input} />

              <label style={{ ...styles.label, marginTop: "12px" }}>{t("mobile.streetColony")}</label>
              <input type="text" name="street" value={drop.street} onChange={handleDropChange} placeholder={t("mobile.egStreet")} style={styles.input} />

              <label style={{ ...styles.label, marginTop: "12px" }}>{t("mobile.landmark")}</label>
              <input type="text" name="landmark" value={drop.landmark} onChange={handleDropChange} placeholder={t("mobile.egLandmark")} style={styles.input} />

              <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>{t("mobile.city")}</label>
                  <input type="text" name="city" value={drop.city} onChange={handleDropChange} placeholder={t("mobile.egCity")} style={styles.input} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>{t("mobile.pincode")}</label>
                  <input type="text" name="postalCode" value={drop.postalCode} onChange={handleDropChange} placeholder={t("mobile.egPincode")} style={styles.input} />
                </div>
              </div>
            </div>

            <div style={styles.timingBlock}>
              <h3 style={styles.sectionTitle}> {t("mobile.timingPackage")} </h3>

              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}> {t("mobile.dateNeeded")} </label>
                  <input
                    type="date"
                    value={neededDate}
                    onChange={(e) => setNeededDate(e.target.value)}
                    style={styles.input}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}> {t("mobile.timeNeeded")} </label>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <input
                      type="text"
                      maxLength="5"
                      placeholder={t("mobile.egTimeHint")}
                      value={neededTime}
                      onChange={(e) => setNeededTime(formatTimeInput(e.target.value))}
                      style={{ ...styles.input, flex: 1 }}
                    />
                    <select
                      value={neededAmPm}
                      onChange={(e) => setNeededAmPm(e.target.value)}
                      style={{ ...styles.input, width: "68px", flexShrink: 0, padding: "10px 6px", cursor: "pointer" }}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              </div>

              <label style={{ ...styles.label, marginTop: "12px" }}> {t("mobile.packageDetails")} </label>
              <textarea 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)} 
                placeholder={t("mobile.egCourierNotes")} 
                style={styles.textarea} 
              />
            </div>

            {error && <p style={styles.error}>{error}</p>}
            
            <button style={{ ...styles.submitBtn, opacity: submitting ? 0.6 : 1 }} onClick={handleSubmit} disabled={submitting}>
              {submitting ? t("mobile.submitting") : t("mobile.submitRequest")}
            </button>
          </div>
        )}
      </div>
    </div>
    </ThemeProvider>
  );
}

const styles = {
  page: { backgroundColor: COLOR.mist, minHeight: "100vh" }, 
  scrollArea: { padding: "0 15px 90px", boxSizing: "border-box" },
  card: { background: COLOR.paper, borderRadius: "14px", padding: "16px", boxShadow: SHADOW.soft, border: `1px solid ${COLOR.line}` },
  heading: { margin: "0 0 8px 0", color: COLOR.ink, fontSize: "20px", fontWeight: "800" },
  subheading: { color: COLOR.muted, fontSize: "13px", marginBottom: "15px", lineHeight: 1.5 },
  
  noteCard: { backgroundColor: COLOR.primarySoft, border: `1px solid ${COLOR.primary}`, borderRadius: "12px", padding: "16px", marginBottom: "16px" },
  noteTitle: { margin: "0 0 8px 0", fontSize: "14px", fontWeight: "800", color: COLOR.primaryDark, display: "flex", alignItems: "center" },
  noteText: { margin: "0 0 10px 0", fontSize: "13px", color: COLOR.ink, lineHeight: "1.5" },
  noteContactBox: { backgroundColor: COLOR.mist, borderRadius: "8px", padding: "10px", display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${COLOR.line}` },
  noteContactLabel: { fontSize: "12px", color: COLOR.primaryDark, fontWeight: "600" },
  noteContactNumber: { fontSize: "14px", fontWeight: "800", color: COLOR.primaryDark },
  instructionBanner: { backgroundColor: COLOR.popSoft, borderLeft: `4px solid ${COLOR.pop}`, padding: "12px 14px", marginBottom: "16px", borderRadius: "6px" },
  instructionText: { margin: 0, fontSize: "13px", color: COLOR.pop, lineHeight: "1.5" },

  contactBlock: { backgroundColor: COLOR.mist, border: `1px solid ${COLOR.line}`, borderRadius: "12px", padding: "16px", marginBottom: "16px" },
  pickupBlock: { backgroundColor: COLOR.mist, border: `1px solid ${COLOR.line}`, borderRadius: "12px", padding: "16px", marginBottom: "16px" },
  dropBlock: { backgroundColor: COLOR.mist, border: `1px solid ${COLOR.line}`, borderRadius: "12px", padding: "16px", marginBottom: "16px" },
  timingBlock: { backgroundColor: COLOR.paper, border: `1px solid ${COLOR.line}`, borderRadius: "12px", padding: "16px", marginBottom: "10px" },

  sectionTitle: { color: COLOR.ink, fontSize: "15px", marginTop: "0", marginBottom: "12px", fontWeight: "700" },
  label: { display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "6px", color: COLOR.ink },
  input: { width: "100%", padding: "10px", borderRadius: "8px", border: `1px solid ${COLOR.line}`, fontSize: "14px", boxSizing: "border-box", backgroundColor: COLOR.paper, outlineColor: COLOR.primary, color: COLOR.ink },
  textarea: { width: "100%", height: "80px", padding: "10px", borderRadius: "8px", border: `1px solid ${COLOR.line}`, fontSize: "14px", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", backgroundColor: COLOR.paper, outlineColor: COLOR.primary, color: COLOR.ink },
  
  error: { color: COLOR.error, fontSize: "14px", marginTop: "14px", fontWeight: "600", textAlign: "center" },
  submitBtn: { width: "100%", marginTop: "15px", padding: "14px", border: "none", borderRadius: "10px", background: COLOR.primaryDark, color: COLOR.onPrimary, fontWeight: "bold", fontSize: "16px", cursor: "pointer", boxShadow: SHADOW.cta },

  confirmCard: { background: COLOR.paper, borderRadius: "14px", padding: "30px 20px", textAlign: "center", boxShadow: SHADOW.soft, border: `1px solid ${COLOR.line}` },
  confirmIcon: { width: "60px", height: "60px", borderRadius: "50%", background: COLOR.successSoft, color: COLOR.success, fontSize: "30px", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" },
  confirmText: { color: COLOR.muted, lineHeight: 1.6, margin: "12px 0 24px" },

  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: COLOR.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 },
  loginBox: { background: COLOR.paper, padding: "20px", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: SHADOW.float },
  closeBtn: { position: "absolute", top: "10px", right: "15px", border: "none", background: "none", fontSize: "24px", cursor: "pointer", color: COLOR.ink, zIndex: 10 }
};